import React, { useEffect, useRef, useState } from 'react';
import { DbConnection } from './module_bindings';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''; 
const DB_NAME = "osint-backend-dqo2v";
const JET_SVG_URI = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iIzAwZmYwMCI+PHBhdGggZD0iTTIxIDE2di0ybC04LTVWMy41YzAtLjgzLS42Ny0xLjUtMS41LTEuNVMxMCAyLjY3IDEwIDMuNVY5bC04IDV2Mmw4LTIuNVYxOWwtMiAxLjVWMjJsMy41LTEgMy41IDF2LTEuNUwxMyAxOXYtNS41bDggMi41eiIvPjwvc3ZnPg==';

// Command Center Coordinates: 10 Malamig, Diliman, Quezon City
const C2_LAT = 14.6433;
const C2_LON = 121.0560;
const C2_LOCATION_NAME = "10 MALAMIG, DILIMAN, QC (J3W3+CW)";

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

interface TelemetryLog {
  id: string;
  timestamp: string;
  type: 'ASSET' | 'EVENT' | 'ARCHIVE';
  summary: string;
  isHistorical?: boolean;
}

const ORBITAL_ASSETS = [
  { id: 'USA-276', type: 'US SPACE FORCE RECON', alt: 400000, inc: 50, speed: 0.06, node: 0 },
  { id: 'YAOGAN-31', type: 'CN PLA RECONNAISSANCE', alt: 600000, inc: 60, speed: 0.055, node: 90 },
  { id: 'KHALIFASAT', type: 'UAE EARTH OBSERVATION', alt: 613000, inc: 97, speed: 0.06, node: 200 }
];

export default function App() {
  const cesiumContainer = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null); 
  
  const [hudData, setHudData] = useState<Record<string, string | number> | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [liveLogs, setLiveLogs] = useState<TelemetryLog[]>([]);
  const [archiveLogs, setArchiveLogs] = useState<TelemetryLog[]>([]);
  const [sigintLogs, setSigintLogs] = useState<{time: string, text: string}[]>([]);
  const [metrics, setMetrics] = useState({ assets: 0, activeEvents: 0, historicalEvents: 0 });
  const flightPaths = useRef<Record<string, any[]>>({});

  const [showFlights, setShowFlights] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  const [showArchive, setShowArchive] = useState(false); 
  const [showOrbits, setShowOrbits] = useState(true); 
  const [showDossier, setShowDossier] = useState(false); 
  const [autoTrack, setAutoTrack] = useState(false); 
  const [zuluTime, setZuluTime] = useState<string>('');
  const [mouseCoords, setMouseCoords] = useState({ lat: '00.0000', lon: '00.0000' });
  const [isOrbiting, setIsOrbiting] = useState(false);
  
  const isOrbitingRef = useRef(isOrbiting);
  const autoTrackRef = useRef(autoTrack);
  const showOrbitsRef = useRef(showOrbits);
  const showFlightsRef = useRef(showFlights);
  const showEventsRef = useRef(showEvents);
  const showArchiveRef = useRef(showArchive);
  const metricsRef = useRef(metrics);

  useEffect(() => { isOrbitingRef.current = isOrbiting; }, [isOrbiting]);
  useEffect(() => { autoTrackRef.current = autoTrack; }, [autoTrack]);
  useEffect(() => { showOrbitsRef.current = showOrbits; }, [showOrbits]);
  useEffect(() => { showFlightsRef.current = showFlights; }, [showFlights]);
  useEffect(() => { showEventsRef.current = showEvents; }, [showEvents]);
  useEffect(() => { showArchiveRef.current = showArchive; }, [showArchive]);
  useEffect(() => { metricsRef.current = metrics; }, [metrics]);

  // Mathematical Geofence Filter: Only accepts data within Philippine borders
  const isWithinPhilippines = (lat: number, lon: number) => {
    return lat >= 4.0 && lat <= 22.0 && lon >= 116.0 && lon <= 127.0;
  };

  const getDefconLevel = () => {
    if (metrics.activeEvents > 15) return { level: 1, color: '#ff0000', text: 'CRITICAL CYBER THREAT' };
    if (metrics.activeEvents > 10) return { level: 2, color: '#ff5500', text: 'HIGH READINESS' };
    if (metrics.activeEvents > 5) return { level: 3, color: '#ffaa00', text: 'ELEVATED READINESS' };
    if (metrics.activeEvents > 0) return { level: 4, color: '#ffff00', text: 'ABOVE NORMAL' };
    return { level: 5, color: '#00ff00', text: 'NORMAL READINESS' };
  };
  const defcon = getDefconLevel();

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().toLocaleTimeString();
      const phrases = [
        "MAINTAINING UPLINK WITH CICC INFRASTRUCTURE...",
        "MONITORING 0xKLAMMER ENDPOINT VECTORS...",
        "BLUEFARROW THREAT SIGNATURES SCANNED - NEGATIVE CONTACT.",
        "AERIAL ASSET TRANSPONDER SIGNATURE VERIFIED IN PH AIRSPACE.",
        "SCANNING ORBITAL ASSETS OVER WEST PHILIPPINE SEA...",
        `UPDATING QUEZON CITY COMMAND NODE (J3W3+CW)...`,
        `TRACKING ${metricsRef.current.assets} ACTIVE ASSETS.`,
        `MAINTAINING ${metricsRef.current.activeEvents} ACTIVE CYBER THREAT VECTORS.`
      ];
      const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)];
      setSigintLogs(prev => [{ time: now, text: randomPhrase }, ...prev].slice(0, 6));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setZuluTime(now.toISOString().replace('T', ' ').substring(0, 19) + ' ZULU');
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!viewerRef.current) return;
    const entities = viewerRef.current.entities.values;
    const now = Date.now();
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      if (entity.id.startsWith('flight-')) entity.show = showFlights;
      if (entity.id.startsWith('orbital-')) entity.show = showOrbits;
      if (entity.id.startsWith('event-')) {
        const eventTime = entity.rawTelemetry?.EVENT_TIME || now;
        const isOld = (now - eventTime) > TWENTY_FOUR_HOURS_MS;
        entity.show = isOld ? showArchive : showEvents;
      }
    }
  }, [showFlights, showEvents, showArchive, showOrbits]);

  const flyToTheater = (lon: number, lat: number, height: number) => {
    if (!viewerRef.current) return;
    // @ts-ignore
    const Cesium = window.Cesium;
    viewerRef.current.trackedEntity = undefined; 
    viewerRef.current.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, height),
      duration: 1.5
    });
  };

  const lockTarget = () => {
    if (!viewerRef.current || !selectedEntityId) return;
    const entity = viewerRef.current.entities.getById(selectedEntityId);
    if (entity) {
      viewerRef.current.trackedEntity = entity;
      setIsOrbiting(false); 
    }
  };

  const breakLock = () => {
    if (!viewerRef.current) return;
    viewerRef.current.trackedEntity = undefined;
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; 
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  };

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      console.error("Critical Failure: Maps API Key is missing.");
      return;
    }

    // @ts-ignore
    const Cesium = window.Cesium;
    Cesium.GoogleMaps.defaultApiKey = GOOGLE_MAPS_API_KEY;

    const viewer = new Cesium.Viewer(cesiumContainer.current, {
      globe: false, animation: false, timeline: false, navigationHelpButton: false,
      sceneModePicker: false, baseLayerPicker: false, geocoder: false,
      homeButton: false, fullscreenButton: false, infoBox: false, selectionIndicator: true 
    });
    
    viewerRef.current = viewer;

    const mountGlobe = async () => {
      try {
        const tileset = await Cesium.createGooglePhotorealistic3DTileset();
        viewer.scene.primitives.add(tileset);
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(121.0560, 14.6433, 4000000), 
          duration: 0 
        });
      } catch (error) {
        console.error("Tactical Map Render Failed.", error);
      }
    };
    mountGlobe();

    viewer.clock.onTick.addEventListener(() => {
      if (isOrbitingRef.current && viewer.scene.mode === Cesium.SceneMode.SCENE3D && !viewer.trackedEntity) {
        viewer.scene.camera.rotateRight(0.0005);
      }

      const time = Date.now() / 1000;
      ORBITAL_ASSETS.forEach(sat => {
        const v = ((time * sat.speed) + sat.node) % 360;
        const vRad = v * (Math.PI / 180);
        const iRad = sat.inc * (Math.PI / 180);
        const latRad = Math.asin(Math.sin(vRad) * Math.sin(iRad));
        const lat = latRad * (180 / Math.PI);
        const dLonRad = Math.atan2(Math.cos(iRad) * Math.sin(vRad), Math.cos(vRad));
        let dLon = dLonRad * (180 / Math.PI);
        const earthRot = (time * 0.00416) % 360;
        let lon = (sat.node + dLon - earthRot) % 360;
        
        if (lon > 180) lon -= 360;
        if (lon < -180) lon += 360;
        
        const entity = viewerRef.current.entities.getById(`orbital-${sat.id}`);
        const pos = Cesium.Cartesian3.fromDegrees(lon, lat, sat.alt);
        
        if (entity) {
          entity.position = pos;
          entity.show = showOrbitsRef.current;
          entity.rawTelemetry.LAT_LON = `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
        } else {
          const newEntity = viewerRef.current.entities.add({
            id: `orbital-${sat.id}`,
            position: pos,
            show: showOrbitsRef.current,
            point: { pixelSize: 8, color: Cesium.Color.CYAN, outlineColor: Cesium.Color.WHITE, outlineWidth: 1 }
          });
          newEntity.rawTelemetry = {
            CLASSIFICATION: "ORBITAL ASSET",
            TARGET_ID: sat.id,
            TYPE: sat.type,
            LAT_LON: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
            ALTITUDE: `${(sat.alt / 1000).toFixed(1)} KM`,
            STATUS: "ACTIVE ORBIT"
          };
        }
      });
    });

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    
    handler.setInputAction((click: any) => {
      const pickedObjects = viewer.scene.drillPick(click.position);
      let foundTelemetry = null;
      let foundId = null;
      
      for (let i = 0; i < pickedObjects.length; i++) {
        const picked = pickedObjects[i];
        if (Cesium.defined(picked) && picked.id && picked.id.rawTelemetry) {
          foundTelemetry = picked.id.rawTelemetry;
          foundId = picked.id.id; 
          
          const coords = foundTelemetry.LAT_LON.split(', ');
          const targetLat = parseFloat(coords[0]);
          const targetLon = parseFloat(coords[1]);
          foundTelemetry.DISTANCE_TO_COMMAND_NODE = `${calculateDistance(C2_LAT, C2_LON, targetLat, targetLon)} KM`;
          break;
        }
      }
      
      setHudData(foundTelemetry);
      setSelectedEntityId(foundId);
      
      if (!foundTelemetry) viewer.trackedEntity = undefined;
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // FIXED: Pure Ellipsoid Math restored for absolute stability
    handler.setInputAction((movement: any) => {
      if (!viewerRef.current) return;
      const cartesian = viewerRef.current.camera.pickEllipsoid(movement.endPosition, viewerRef.current.scene.globe.ellipsoid);
      if (cartesian) {
        const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
        setMouseCoords({
          lon: Cesium.Math.toDegrees(cartographic.longitude).toFixed(4),
          lat: Cesium.Math.toDegrees(cartographic.latitude).toFixed(4)
        });
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    const conn = DbConnection.builder()
      .withUri('wss://maincloud.spacetimedb.com')
      .withDatabaseName(DB_NAME)
      .onConnect((conn) => {
        conn.subscriptionBuilder()
          .onApplied(() => {
            const allEvents = Array.from(conn.db.geopolitical_event.iter());
            const now = Date.now();
            let active = 0; let historical = 0;
            allEvents.forEach(e => {
              // Apply the Geofence Purge: Ignore any event not in the Philippines
              if (e.category.includes(' // ') && isWithinPhilippines(e.lat, e.lon)) {
                const isOld = (now - Number(e.id)) > TWENTY_FOUR_HOURS_MS;
                if (isOld) historical++; else active++;
              }
            });
            setMetrics({
              assets: Array.from(conn.db.aerial_asset.iter()).filter(a => isWithinPhilippines(a.lat, a.lon)).length,
              activeEvents: active, historicalEvents: historical
            });
          })
          .subscribe(["SELECT * FROM aerial_asset", "SELECT * FROM geopolitical_event"]);
      })
      .build(); 

    conn.db.aerial_asset.onInsert((ctx, asset) => {
      if (!isWithinPhilippines(asset.lat, asset.lon)) return;

      const startPos = Cesium.Cartesian3.fromDegrees(asset.lon, asset.lat, asset.altitude || 15000);
      flightPaths.current[asset.id] = [startPos];

      const entity = viewer.entities.add({
        id: `flight-${asset.id}`,
        position: startPos, show: showFlightsRef.current,
        billboard: { image: JET_SVG_URI, width: 24, height: 24, color: Cesium.Color.LIME },
        polyline: {
          positions: flightPaths.current[asset.id],
          width: 2, material: new Cesium.PolylineDashMaterialProperty({ color: Cesium.Color.LIME.withAlpha(0.6), dashLength: 15.0 })
        }
      });
      
      (entity as any).rawTelemetry = {
        CLASSIFICATION: "AERIAL ASSET", TARGET_ID: asset.id,
        LAT_LON: `${asset.lat.toFixed(4)}, ${asset.lon.toFixed(4)}`, ALTITUDE: `${Math.round(asset.altitude || 0)}m`
      };

      if (ctx.event) {
        setMetrics(prev => ({ ...prev, assets: prev.assets + 1 }));
        setLiveLogs(prev => {
          const newLog: TelemetryLog = {
            id: `f-${asset.id}-${Date.now()}`,
            timestamp: new Date().toLocaleTimeString(),
            type: 'ASSET', summary: `Flight ${asset.id.substring(0,6)} entered airspace`
          };
          return [newLog, ...prev].slice(0, 15);
        });
      }
    });

    conn.db.aerial_asset.onUpdate((ctx, oldAsset, newAsset) => {
      const entity = viewer.entities.getById(`flight-${newAsset.id}`);
      if (entity && isWithinPhilippines(newAsset.lat, newAsset.lon)) {
        const newPos = Cesium.Cartesian3.fromDegrees(newAsset.lon, newAsset.lat, newAsset.altitude || 15000);
        // @ts-ignore
        entity.position = newPos;
        
        if (flightPaths.current[newAsset.id]) {
          flightPaths.current[newAsset.id].push(newPos);
          if (flightPaths.current[newAsset.id].length > 8) flightPaths.current[newAsset.id].shift();
          // @ts-ignore
          entity.polyline.positions = flightPaths.current[newAsset.id];
        }
        (entity as any).rawTelemetry.LAT_LON = `${newAsset.lat.toFixed(4)}, ${newAsset.lon.toFixed(4)}`;
        (entity as any).rawTelemetry.ALTITUDE = `${Math.round(newAsset.altitude || 0)}m`;
      }
    });

    conn.db.aerial_asset.onDelete((ctx, asset) => {
      if (!isWithinPhilippines(asset.lat, asset.lon)) return;
      viewer.entities.removeById(`flight-${asset.id}`);
      delete flightPaths.current[asset.id];
      if (ctx.event) setMetrics(prev => ({ ...prev, assets: Math.max(0, prev.assets - 1) }));
    });

    conn.db.geopolitical_event.onInsert((ctx, event) => {
      // GEOFENCE PURGE: If the event is not inside the Philippines, mathematically ignore it.
      if (!event.category.includes(' // ') || !isWithinPhilippines(event.lat, event.lon)) return;

      const eventTime = Number(event.id);
      const isHistorical = (Date.now() - eventTime) > TWENTY_FOUR_HOURS_MS;
      
      const entity = viewer.entities.add({
        id: `event-${event.id}`,
        position: Cesium.Cartesian3.fromDegrees(event.lon, event.lat, 0), 
        show: isHistorical ? showArchiveRef.current : showEventsRef.current,
        point: { 
          pixelSize: 8, 
          color: isHistorical ? Cesium.Color.fromCssColorString('#ffaa00').withAlpha(0.5) : Cesium.Color.RED, 
          outlineColor: isHistorical ? Cesium.Color.fromCssColorString('#ffaa00').withAlpha(0.2) : Cesium.Color.WHITE, 
          outlineWidth: 1 
        }
      });
      
      const taxonomyClass = event.category.split(' // ')[0];
      const taxonomyDetail = event.category.split(' // ')[1];

      // Factual data only. Roleplay mapping has been purged.
      (entity as any).rawTelemetry = {
        CLASSIFICATION: taxonomyClass, 
        DETAIL: taxonomyDetail,
        SEVERITY: `Level ${event.severity}`, 
        LAT_LON: `${event.lat.toFixed(4)}, ${event.lon.toFixed(4)}`,
        EVENT_TIME: eventTime,
        STATUS: isHistorical ? "ARCHIVED DECAY" : "ACTIVE THREAT"
      };

      if (ctx.event) {
        const dateObj = new Date(eventTime);
        const logData: TelemetryLog = {
          id: `e-${event.id}-${Date.now()}`,
          timestamp: dateObj.toLocaleTimeString(),
          type: isHistorical ? 'ARCHIVE' : 'EVENT',
          summary: `Lvl ${event.severity} ${taxonomyClass} logged at ${event.lat.toFixed(2)}, ${event.lon.toFixed(2)}`,
          isHistorical: isHistorical
        };

        if (isHistorical) {
          setMetrics(prev => ({ ...prev, historicalEvents: prev.historicalEvents + 1 }));
          setArchiveLogs(prev => [logData, ...prev].sort((a, b) => b.timestamp.localeCompare(a.timestamp)));
        } else {
          setMetrics(prev => ({ ...prev, activeEvents: prev.activeEvents + 1 }));
          setLiveLogs(prev => [logData, ...prev].slice(0, 15));

          if (autoTrackRef.current && viewerRef.current) {
            setIsOrbiting(false);
            viewerRef.current.trackedEntity = undefined;
            viewerRef.current.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(event.lon, event.lat, 2500000),
              duration: 2.0
            });
            setSelectedEntityId(entity.id);
            setHudData((entity as any).rawTelemetry);
          }
        }
      }
    });

    return () => {
      handler.destroy();
      conn.disconnect();
      viewer.destroy();
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', backgroundColor: '#000', fontFamily: 'monospace', overflow: 'hidden' }}>
      <style>{`
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: rgba(0, 15, 0, 0.9); }
        ::-webkit-scrollbar-thumb { background: #00ff00; border-radius: 0px; }
        
        @keyframes scanline { 0% { transform: translateY(-100%); } 100% { transform: translateY(100vh); } }
        @keyframes spinTarget { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(360deg); } }
        @keyframes spinTargetReverse { 0% { transform: rotate(0deg); } 100% { transform: rotate(-360deg); } }
        @keyframes pulseDefcon { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }
        @keyframes typeWriter { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }

        .crt-overlay {
          pointer-events: none; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 5;
          background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06));
          background-size: 100% 2px, 3px 100%;
        }
        .scanner {
          position: absolute; top: 0; left: 0; width: 100%; height: 5px; 
          background: rgba(0, 255, 0, 0.1); box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
          animation: scanline 8s linear infinite; z-index: 6; pointer-events: none;
        }

        .gods-eye-reticle {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 400px; height: 400px; border-radius: 50%;
          border: 1px dashed rgba(0, 255, 0, 0.4);
          animation: spinTarget 40s linear infinite; pointer-events: none; z-index: 4;
        }
        .gods-eye-reticle::before {
          content: ''; position: absolute; top: 20px; left: 20px; right: 20px; bottom: 20px;
          border-radius: 50%; border: 2px solid rgba(0, 255, 0, 0.1);
          border-top-color: rgba(0, 255, 0, 0.6); border-bottom-color: rgba(0, 255, 0, 0.6);
          animation: spinTargetReverse 20s linear infinite;
        }

        .corner-brackets { position: relative; }
        .corner-brackets::before, .corner-brackets::after { content: ''; position: absolute; width: 10px; height: 10px; border-color: #00ff00; border-style: solid; }
        .corner-brackets::before { top: 0; left: 0; border-width: 2px 0 0 2px; }
        .corner-brackets::after { bottom: 0; right: 0; border-width: 0 2px 2px 0; }
        
        .tac-btn {
          background: rgba(0, 255, 0, 0.1); border: 1px solid #00ff00; color: #00ff00;
          padding: 8px 12px; cursor: pointer; font-family: monospace; font-size: 11px;
          text-transform: uppercase; transition: all 0.2s;
        }
        .tac-btn:hover { background: #00ff00; color: #000; }
        .tac-btn.active { background: rgba(0, 255, 0, 0.3); border: 1px solid #fff; color: #fff; box-shadow: 0 0 10px rgba(0,255,0,0.4); }
        .action-btn {
          background: rgba(255, 0, 0, 0.1); border: 1px solid red; color: red;
          padding: 8px; cursor: pointer; font-family: monospace; font-size: 11px; font-weight: bold; width: 100%; margin-top: 10px;
        }
        .action-btn:hover { background: red; color: #fff; }
        
        .log-entry { animation: typeWriter 0.3s ease-out forwards; }
      `}</style>

      <div ref={cesiumContainer} style={{ width: '100%', height: '100%', margin: 0, padding: 0 }} />
      <div className="crt-overlay" />
      <div className="scanner" />
      <div className="gods-eye-reticle" />

      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '40px', height: '40px', pointerEvents: 'none', zIndex: 6, opacity: 0.8
      }}>
        <div style={{ position: 'absolute', top: '0', left: '19px', width: '2px', height: '15px', backgroundColor: '#00ff00' }} />
        <div style={{ position: 'absolute', bottom: '0', left: '19px', width: '2px', height: '15px', backgroundColor: '#00ff00' }} />
        <div style={{ position: 'absolute', top: '19px', left: '0', width: '15px', height: '2px', backgroundColor: '#00ff00' }} />
        <div style={{ position: 'absolute', top: '19px', right: '0', width: '15px', height: '2px', backgroundColor: '#00ff00' }} />
      </div>

      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '40px',
        backgroundColor: 'rgba(0, 15, 0, 0.9)', borderBottom: '1px solid #00ff00',
        display: 'flex', alignItems: 'center', padding: '0 20px', color: '#00ff00',
        justifyContent: 'space-between', zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ fontWeight: 'bold', letterSpacing: '2px' }}>CICC OVERWATCH :: NODE SECURE</div>
          <div style={{ 
            padding: '2px 8px', border: `1px solid ${defcon.color}`, color: defcon.color, 
            fontWeight: 'bold', animation: defcon.level < 4 ? 'pulseDefcon 2s infinite' : 'none' 
          }}>
            DEFCON {defcon.level} : {defcon.text}
          </div>
        </div>
        
        <div style={{ fontWeight: 'bold', letterSpacing: '1px', opacity: 0.8 }}>{zuluTime}</div>
      </div>

      <div className="corner-brackets" style={{
        position: 'absolute', top: '55px', left: '15px', bottom: '280px', width: '320px',
        backgroundColor: 'rgba(0, 15, 0, 0.85)', padding: '15px', overflowY: 'auto', color: '#00ff00', zIndex: 10,
        boxShadow: '0 0 10px rgba(0, 255, 0, 0.1)'
      }}>
        <h3 style={{ marginTop: 0, borderBottom: `1px dotted ${showArchive ? '#ffaa00' : '#00ff00'}`, paddingBottom: '10px', color: showArchive ? '#ffaa00' : '#00ff00' }}>
          {showArchive ? 'ARCHIVED INCIDENTS' : 'LIVE CYBER THREAT FEED'}
        </h3>
        
        {(!showArchive && liveLogs.length === 0) || (showArchive && archiveLogs.length === 0) ? 
          <div style={{ opacity: 0.5 }}>Awaiting Philippine Telemetry...</div> : null}
        
        {(showArchive ? archiveLogs : liveLogs).map(log => (
          <div key={log.id} className="log-entry" style={{
            marginBottom: '10px', fontSize: '12px', padding: '8px',
            backgroundColor: log.type === 'EVENT' ? 'rgba(255,0,0,0.1)' : (log.type === 'ARCHIVE' ? 'rgba(255,170,0,0.05)' : 'rgba(0,255,0,0.05)'),
            borderLeft: `3px solid ${log.type === 'EVENT' ? 'red' : (log.type === 'ARCHIVE' ? '#ffaa00' : '#00ff00')}`, wordWrap: 'break-word'
          }}>
            <div style={{ opacity: 0.7, marginBottom: '4px' }}>[{log.timestamp}]</div>
            <div style={{ lineHeight: '1.4', color: log.type === 'ARCHIVE' ? '#ffaa00' : '#fff' }}>{log.summary}</div>
          </div>
        ))}
      </div>

      {/* THREAT ACTOR DOSSIER (KLAMMER & BLUEFARROW) */}
      {showDossier && (
        <div className="corner-brackets" style={{
          position: 'absolute', top: '55px', right: '380px', width: '360px',
          backgroundColor: 'rgba(0, 15, 0, 0.95)', padding: '15px', color: '#00aaff', zIndex: 10,
          boxShadow: '0 0 15px rgba(0, 170, 255, 0.3)', display: 'flex', flexDirection: 'column'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dotted #00aaff', paddingBottom: '5px', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '13px', color: '#00aaff' }}>THREAT ACTOR DOSSIER</h3>
            <button onClick={() => setShowDossier(false)} style={{ background: 'transparent', border: 'none', color: '#00aaff', cursor: 'pointer' }}>X</button>
          </div>
          
          <div style={{ marginBottom: '15px', borderLeft: '3px solid red', paddingLeft: '10px', backgroundColor: 'rgba(255,0,0,0.1)', padding: '10px' }}>
            <div style={{ fontWeight: 'bold', color: 'red', marginBottom: '4px' }}>TARGET ID: KLAMMER</div>
            <div style={{ fontSize: '11px', color: '#fff', lineHeight: '1.4' }}>
              <strong>ALIAS:</strong> 0xKlammer<br/>
              <strong>KNOWN VECTORS:</strong> Defacement, Unauthorized Access targeting Philippine CICC Infrastructure.<br/>
              <strong>ENDPOINT TRACE:</strong> facebook.com/0xKlammer<br/>
              <strong>STATUS:</strong> ACTIVE THREAT
            </div>
          </div>

          <div style={{ borderLeft: '3px solid red', paddingLeft: '10px', backgroundColor: 'rgba(255,0,0,0.1)', padding: '10px' }}>
            <div style={{ fontWeight: 'bold', color: 'red', marginBottom: '4px' }}>TARGET ID: BLUEFARROW</div>
            <div style={{ fontSize: '11px', color: '#fff', lineHeight: '1.4' }}>
              <strong>ASSOCIATION:</strong> Unknown APT<br/>
              <strong>KNOWN VECTORS:</strong> Correlated with CICC attacks and network breaches.<br/>
              <strong>STATUS:</strong> ACTIVE THREAT
            </div>
          </div>
        </div>
      )}

      {/* SIGINT / ELINT Uplink Terminal */}
      <div className="corner-brackets" style={{
        position: 'absolute', bottom: '15px', left: '350px', right: '380px', height: '140px',
        backgroundColor: 'rgba(0, 15, 0, 0.85)', padding: '10px 15px', color: '#00ff00', 
        zIndex: 10, fontSize: '11px', overflowY: 'hidden', display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ fontWeight: 'bold', borderBottom: '1px dotted #00ff00', paddingBottom: '4px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
          <span>CYBER SIGINT UPLINK</span>
          <span style={{ color: '#00aaff' }}>COMMAND NODE: {C2_LOCATION_NAME}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {sigintLogs.map((log, idx) => (
             <div key={idx} className="log-entry" style={{ opacity: 1 - (idx * 0.15) }}>
               <span style={{ color: '#00aaff' }}>{`>`}</span> [{log.time}] {log.text}
             </div>
          ))}
        </div>
      </div>

      {/* Tactical Command Deck */}
      <div className="corner-brackets" style={{
        position: 'absolute', bottom: '15px', left: '15px', width: '320px', height: '250px',
        backgroundColor: 'rgba(0, 15, 0, 0.9)', padding: '15px', color: '#00ff00', zIndex: 10,
        boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)', display: 'flex', flexDirection: 'column', gap: '10px'
      }}>
        <h3 style={{ margin: 0, borderBottom: '1px dotted #00ff00', paddingBottom: '5px', fontSize: '14px' }}>PHILIPPINE COMMAND</h3>
        
        <div style={{ display: 'flex', gap: '5px' }}>
          <button className={`tac-btn ${showFlights ? 'active' : ''}`} style={{ flex: 1 }} onClick={() => setShowFlights(!showFlights)}>
            {showFlights ? '[X] ASSETS' : '[ ] ASSETS'}
          </button>
          <button className={`tac-btn ${showOrbits ? 'active' : ''}`} style={{ flex: 1, borderColor: '#00aaff', color: showOrbits ? '#fff' : '#00aaff' }} onClick={() => setShowOrbits(!showOrbits)}>
            {showOrbits ? '[X] ORBITS' : '[ ] ORBITS'}
          </button>
          <button className={`tac-btn ${showEvents ? 'active' : ''}`} style={{ flex: 1 }} onClick={() => setShowEvents(!showEvents)}>
            {showEvents ? '[X] THREATS' : '[ ] THREATS'}
          </button>
        </div>

        <button className={`tac-btn ${autoTrack ? 'active' : ''}`} style={{ width: '100%', borderColor: autoTrack ? 'red' : '#00ff00', color: autoTrack ? '#fff' : '#00ff00', backgroundColor: autoTrack ? 'rgba(255,0,0,0.3)' : 'transparent' }} 
          onClick={() => setAutoTrack(!autoTrack)}>
          {autoTrack ? '[X] AUTO-TRACK CYBER THREATS' : '[ ] AUTO-TRACK CYBER THREATS'}
        </button>

        <button className={`tac-btn ${showArchive ? 'active' : ''}`} style={{ width: '100%', borderColor: '#ffaa00', color: showArchive ? '#fff' : '#ffaa00' }} 
          onClick={() => setShowArchive(!showArchive)}>
          {showArchive ? '[X] ARCHIVE VISION ACTIVE' : '[ ] ENABLE ARCHIVE VISION'}
        </button>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button className="tac-btn" style={{ flex: 1 }} onClick={() => flyToTheater(121.0560, 14.6433, 500000)}>NCR NODE</button>
          <button className="tac-btn" style={{ flex: 1 }} onClick={() => flyToTheater(121.6256, 17.0608, 1500000)}>LUZON</button>
          <button className="tac-btn" style={{ flex: 1 }} onClick={() => flyToTheater(122.5186, 10.7410, 1500000)}>VISAYAS</button>
          <button className="tac-btn" style={{ flex: 1 }} onClick={() => flyToTheater(125.4553, 7.1907, 1500000)}>MINDANAO</button>
        </div>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button className="tac-btn" style={{ flex: 1, borderColor: 'red', color: 'red' }} onClick={() => setShowDossier(!showDossier)}>
            [ CICC THREAT DOSSIER ]
          </button>
          <button className="tac-btn" style={{ flex: 1, borderColor: '#ffaa00', color: '#ffaa00' }} 
            onClick={() => { flyToTheater(121.0560, 14.6433, 4000000); setIsOrbiting(false); setShowDossier(false); }}>
            RESET VIEW
          </button>
        </div>
      </div>
      
      {/* Target Data Overlay */}
      {hudData && (
        <div className="corner-brackets" style={{
          position: 'absolute', top: '55px', right: '15px',
          backgroundColor: 'rgba(0, 15, 0, 0.9)', padding: '20px', color: '#00ff00', 
          width: '350px', pointerEvents: 'auto', boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)', zIndex: 10
        }}>
          <h3 style={{ margin: '0 0 15px 0', borderBottom: '1px dotted #00ff00', paddingBottom: '5px' }}>TARGET DATA</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(hudData).map(([key, value]) => {
              if (key === 'EVENT_TIME') return null; 
              return (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', fontSize: '13px' }}>
                  <span style={{ fontWeight: 'bold', marginBottom: '4px', color: key === 'DISTANCE_TO_COMMAND_NODE' ? '#00aaff' : '#00ff00' }}>{key}:</span>
                  <span style={{ color: '#fff', whiteSpace: 'normal', lineHeight: '1.4' }}>{value as string}</span>
                </div>
              );
            })}
          </div>
          
          {selectedEntityId && (selectedEntityId.startsWith('flight') || selectedEntityId.startsWith('orbital')) && (
            <button className="action-btn" onClick={lockTarget}>
              [ LOCK TARGET ]
            </button>
          )}
        </div>
      )}

      {/* Threat Matrix Bar Graph & Scanner Telemetry */}
      <div className="corner-brackets" style={{
        position: 'absolute', bottom: '15px', right: '15px', width: '350px',
        backgroundColor: 'rgba(0, 15, 0, 0.85)', padding: '15px', color: '#00ff00', 
        zIndex: 10, display: 'flex', flexDirection: 'column', gap: '10px'
      }}>
        <div style={{ fontWeight: 'bold', borderBottom: '1px dotted #00ff00', paddingBottom: '4px', fontSize: '13px' }}>SYSTEM DIAGNOSTICS</div>
        
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
            <span>THREAT SATURATION</span>
            <span>{Math.min(100, Math.round((metrics.activeEvents / 30) * 100))}%</span>
          </div>
          <div style={{ width: '100%', height: '6px', backgroundColor: 'rgba(0,255,0,0.2)' }}>
            <div style={{ width: `${Math.min(100, (metrics.activeEvents / 30) * 100)}%`, height: '100%', backgroundColor: defcon.color, transition: 'width 0.5s' }} />
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
            <span>ASSET UPLINKS</span>
            <span>{metrics.assets} TRACKED</span>
          </div>
          <div style={{ width: '100%', height: '6px', backgroundColor: 'rgba(0,255,0,0.2)' }}>
            <div style={{ width: `${Math.min(100, (metrics.assets / 400) * 100)}%`, height: '100%', backgroundColor: '#00aaff', transition: 'width 0.5s' }} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '12px', borderTop: '1px dotted #00ff00', paddingTop: '10px' }}>
          <div>LAT: <span style={{ color: '#fff' }}>{mouseCoords.lat}°</span></div>
          <div>LON: <span style={{ color: '#fff' }}>{mouseCoords.lon}°</span></div>
        </div>
      </div>
    </div>
  );
}
